import { Link } from 'gatsby';
import React, { useState } from 'react';
import {
  ArrowRight,
  Cpu,
  Eye,
  MessageCircle,
  Terminal,
  Zap
} from 'react-feather';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { AiVisibilityAccessDialog } from '../AiVisibilityAccessDialog';
import { Headline } from '../Headline';
import { Kicker } from '../Kicker';
import { Reveal } from '../motion';
import { ComingSoonBadge, EarlyAccessBadge } from '../NewBadge';

const SectionWrapper = styled('section')`
  padding: 100px 24px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 960px) {
    padding: 80px 24px;
  }
`;

const SectionHeader = styled('div')`
  text-align: center;
  margin-bottom: 60px;

  @media (max-width: 960px) {
    margin-bottom: 40px;
  }
`;

const HeaderBadgeRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const SectionDescription = styled('p')`
  font-size: 18px;
  line-height: 1.6;
  color: #4f4f4f;
  margin-top: 16px;
  max-width: 780px;
  margin-left: auto;
  margin-right: auto;
`;

const FeatureGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled('div')`
  background-color: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(24, 144, 255, 0.12);
    transform: translateY(-2px);
  }
`;

const IconWrapper = styled('div')`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background-color: #e6f7ff;
  color: ${COLORS.PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FeatureTitle = styled('h3')`
  font-size: 18px;
  font-weight: 700;
  color: #0e1c2b;
  margin: 0;
`;

const FeatureDescription = styled('p')`
  font-size: 15px;
  line-height: 1.6;
  color: #4f4f4f;
  margin: 0;
  flex: 1;
`;

const UseCase = styled('div')`
  font-size: 13px;
  color: #64748b;
  font-style: italic;
  border-left: 3px solid #e6f7ff;
  padding-left: 12px;
`;

const ClientChips = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ClientChip = styled('span')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 12px;
  font-weight: 600;
  color: #0e1c2b;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 100px;
  padding: 4px 12px;
`;

const ExploreLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const ExploreRow = styled('div')`
  display: flex;
  justify-content: center;
  margin-top: 40px;
`;

const VisibilityTeaser = styled('div')`
  margin-top: 24px;
  background-color: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
`;

const VisibilityTeaserText = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 640px;
`;

const VisibilityTitleRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  h3 {
    font-size: 18px;
    font-weight: 700;
    color: #0e1c2b;
    margin: 0;
  }
`;

const RequestAccessButton = styled('button')`
  background-color: #0e1c2b;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  border: none;
  border-radius: 100px;
  padding: 12px 28px;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;

  &:hover {
    background-color: #1f3348;
  }
`;

const features = [
  {
    icon: <MessageCircle size={20} />,
    title: 'Affilimate Agent',
    description:
      'Your AI analyst inside Affilimate. Ask about performance, merchants, and product inventory on live data — and get structured answers with links to the underlying reports.',
    useCase: '“Why did revenue drop on our gift guides last week?”'
  },
  {
    icon: <Terminal size={20} />,
    title: 'Affilimate MCP',
    description:
      'Connect your AI tools directly to your Affilimate workspace. Your transaction, finance, and rates data — no code, no exports, no copy-paste.',
    useCase: null,
    chips: ['Claude', 'ChatGPT', 'Codex', 'Cursor', 'Gemini']
  },
  {
    icon: <Cpu size={20} />,
    title: 'Agentic campaign management',
    description:
      'Describe a sponsorship in chat and get a campaign draft scoped to relevant pages, aligned with your editorial standards. You review and launch — never autonomous.',
    useCase: '“I didn\u2019t scope the campaign — I reviewed it.”'
  },
  {
    icon: <Eye size={20} />,
    title: 'Agent analytics for commerce content',
    description:
      'See how AI systems interact with the content you monetize, on the same pages where you track clicks and commissions.',
    useCase: '“Which of our top-earning pages are AI assistants reading?”'
  },
  {
    icon: <Zap size={20} />,
    title: 'Yield Optimizer',
    description:
      'Cross-merchant opportunities surfaced from your catalog-linked inventory, with AI-powered campaign suggestions ready for review at the top of the page.',
    useCase: '“$14K/mo in swap opportunities — 11 pages already scoped.”'
  }
];

export const AiPlatformSection = () => {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <SectionWrapper id="ai-platform">
      <SectionHeader>
        <Reveal>
          <Kicker>Agent · MCP · Agentic Campaigns</Kicker>
          <HeaderBadgeRow>
            <Headline component="h2" style={{ margin: 0 }}>
              One AI platform across everything you monetize
            </Headline>
            <EarlyAccessBadge />
          </HeaderBadgeRow>
          <SectionDescription>
            Affilimate has spent years building the analytics and campaign
            infrastructure premium publishers run on. The AI platform is the
            next stage: the same trusted data, now working for you — answering
            questions, surfacing opportunities, and drafting campaigns your
            team reviews.
          </SectionDescription>
        </Reveal>
      </SectionHeader>
      <FeatureGrid>
        {features.map((feature, index) => (
          <Reveal key={feature.title} delay={(index % 3) * 120}>
            <FeatureCard>
              <IconWrapper>{feature.icon}</IconWrapper>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
              {feature.chips && (
                <ClientChips>
                  {feature.chips.map((chip) => (
                    <ClientChip key={chip}>{chip}</ClientChip>
                  ))}
                </ClientChips>
              )}
              {feature.useCase && <UseCase>{feature.useCase}</UseCase>}
            </FeatureCard>
          </Reveal>
        ))}
        <Reveal delay={240}>
          <FeatureCard
            style={{
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              backgroundColor: '#0e1c2b',
              border: 'none'
            }}
          >
            <ExploreLink
              to="/ai-platform/"
              style={{ color: '#69c0ff', fontSize: 18 }}
            >
              Explore the AI platform <ArrowRight size={20} />
            </ExploreLink>
          </FeatureCard>
        </Reveal>
      </FeatureGrid>
      <Reveal delay={120}>
        <VisibilityTeaser>
          <VisibilityTeaserText>
            <VisibilityTitleRow>
              <h3>AI visibility campaigns</h3>
              <ComingSoonBadge />
            </VisibilityTitleRow>
            <FeatureDescription>
              Most AI visibility tools are built for brands watching their own
              mentions. This is built for publishers — discover where your
              influence is building in AI answers, package it into offers
              brands can buy, and run the campaigns end to end.
            </FeatureDescription>
          </VisibilityTeaserText>
          <RequestAccessButton onClick={() => setDialogOpen(true)}>
            Request early access
          </RequestAccessButton>
        </VisibilityTeaser>
      </Reveal>
      <AiVisibilityAccessDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </SectionWrapper>
  );
};
